import { createApp } from 'vue';
import App from '../App.vue';
import router from '../router';
import { createPinia } from 'pinia';
import '../assets/tailwind.css';
import '../assets/variables.css';  // 添加全局变量样式

import 'primeicons/primeicons.css'

import PrimeVue from 'primevue/config';
import { definePreset } from '@primevue/themes';
import Aura from '@primevue/themes/aura';
import { i18n } from '../i18n';

import { WebPlatform } from '../platforms/web';
import Button from 'primevue/button';
import { createHead } from '@vueuse/head';
import { bindSystemThemeListener, initThemeMode } from '@/composables/useThemeMode';
import registerFeatureUnlockSyncProvider from '@/core/bootstrap/registerFeatureUnlockSyncProvider';
import { useUserStore } from '@/core/store/useUserStore';

// 0.8.2 Phase A: 先应用主题，避免首屏闪烁
initThemeMode();
bindSystemThemeListener();

// 创建 Vue 应用实例
const app = createApp(App);

// 创建并使用head实例
const head = createHead();
app.use(head);

// 使用 Pinia 和 Router
const pinia = createPinia();
app.use(pinia);
registerFeatureUnlockSyncProvider(pinia);
app.use(router);
app.use(i18n);

// AUTH-01：Pinia 安装完成后注册一次 Supabase 跨标签页认证事件监听，
// 使其他标签页的登入 / 登出能同步到 Pinia 用户态与订阅态。
const userStore = useUserStore(pinia);
const authUnsubscribe = userStore.setupAuthListener();

// 配置 PrimeVue 主题
const MyPreset = definePreset(Aura, {
    // 0.8.1 Phase A: PrimeVue 语义色从统一主题 token 取值
    semantic: {
        primary: {
            50: 'var(--theme-primary-soft-bg)',
            100: 'var(--theme-primary-soft-bg)',
            200: 'var(--theme-primary-soft-bg)',
            300: 'var(--theme-gradient-start)',
            400: 'var(--theme-gradient-start)',
            500: 'var(--theme-primary)',
            600: 'var(--theme-primary)',
            700: 'var(--theme-primary-hover)',
            800: 'var(--theme-primary-hover)',
            900: 'var(--theme-primary-hover)',
            950: 'var(--theme-primary-hover)'
        },
        colorScheme: {
            light: {
                primary: {
                    color: 'var(--theme-primary)',
                    inverseColor: 'var(--text-inverse)',
                    hoverColor: 'var(--theme-primary-hover)',
                    activeColor: 'var(--theme-primary-hover)'
                },
                highlight: {
                    background: 'var(--theme-primary-soft-bg)',
                    focusBackground: 'var(--theme-primary-soft-bg)',
                    color: 'var(--theme-primary-soft-text)',
                    focusColor: 'var(--theme-primary-soft-text)'
                }
            },
            dark: {
                primary: {
                    color: 'var(--theme-primary)',
                    inverseColor: 'var(--text-inverse)',
                    hoverColor: 'var(--theme-primary-hover)',
                    activeColor: 'var(--theme-primary-hover)'
                },
                highlight: {
                    background: 'color-mix(in srgb, var(--theme-primary) 22%, var(--surface-card))',
                    focusBackground: 'color-mix(in srgb, var(--theme-primary) 28%, var(--surface-card))',
                    color: 'var(--text-primary)',
                    focusColor: 'var(--text-primary)'
                }
            }
        }
    }
});

// 使用 PrimeVue
app.use(PrimeVue, {
    theme: {
        preset: MyPreset,
        options: {
            darkModeSelector: 'html[data-theme="dark"]'
        }
    }
});

// 注册需要的组件
/* eslint-disable vue/multi-word-component-names */
app.component('Button', Button);
/* eslint-enable vue/multi-word-component-names */

// 创建并注入网页平台实例
const platform = new WebPlatform();
app.provide('platform', platform);

// SEO-R1：在挂载前同步移除预渲染注入的 head 标记（data-prerender-head="true"），
// 避免静态 head 与运行时 @vueuse/head 并存；移除后由页面 useHead 生成唯一运行时 head。
// 不能在 onMounted 中执行，否则客户端与静态 head 会短暂并存（见 Review 报告 §11.4.3）。
document
  .querySelectorAll('[data-prerender-head="true"]')
  .forEach((node) => node.remove())

// Mount the app directly (OpenCV will be loaded on demand for non-Landing pages)
app.mount('#app');

// AUTH-01：页面卸载（含 HMR 重挂载）时注销监听，避免重复注册 / 内存泄漏。
const originalUnmount = app.unmount;
app.unmount = function () {
  authUnsubscribe?.();
  return originalUnmount.apply(this, arguments);
};
